import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { hasValue, isFunction, isNil, useDrawer } from 'swipeclock-frontend';
import { webclockApi } from 'src/services/webclock';
import { useUser } from 'store/userSlice';
import { useBootstrapAuth } from 'components/BootstrapAuthContext';
import { webClockContextKeys } from './constants/webClockContextKeys';
import { useOfflineConnectivitySnackbars } from './hooks/useOfflineConnectivitySnackbars';
import { useOfflineWebClockLifecycle } from './hooks/useOfflineWebClockLifecycle';
import { OfflinePunchContext } from './hooks/useOfflinePunchQueue';
import { useNetworkStatus } from './hooks/useNetworkStatus';
import { useIsOfflineShellMode } from './hooks/useMobileMode';
import { useSubmitOfflinePunch } from './hooks/useWebclock';
import {
  isAuthError,
  isBusinessError,
  isNetworkError,
  isTransportError,
} from './utils/isNetworkError';
import { getNextRetryDelayMs } from './utils/offlinePunchRetrySchedule';
import {
  getApiReachability,
  reportApiUnreachable,
  subscribeApiReachability,
} from './utils/apiReachability';
import { useApiReachability } from './hooks/useApiReachability';
import {
  extendRetryDisplayUntil,
  getRetryDisplayUntil,
  getSubmitDisplayUntil,
  hasFailedOfflinePunch,
} from './utils/offlinePunchDisplayStatus';
import { OFFLINE_PUNCH_QUEUE_MAX_ITEMS } from './constants/offlinePunchSyncConstants';
import {
  readOfflinePunchQueue,
  writeOfflinePunchQueue,
} from './utils/offlinePunchStorage';
import { getWebClockUserStorageKey } from './utils/webClockUserStorageKey';
import { isOfflineSecureStorageWritable } from './utils/offlineWebClockStorage';
import {
  rebuildOfflinePunchPrediction,
  recordCompletedMobilePunch,
} from './utils/offlineClockConnectCache';
import {
  buildQueueItem,
  countPendingItems,
  getEarliestRetryDelayMs,
  getOfflinePunchErrorMessage,
  hasReadyQueueItem,
  isReadyForRetry,
  purgeExpiredItems,
} from './utils/offlinePunchQueueUtils';

const DRAIN_PROGRESS_FIELDS = [
  'status',
  'submitDisplayUntil',
  'retryDisplayUntil',
];

/**
 * Reconcile drain workingQueue with persisted storage after concurrent mutations.
 * Stored queue is authoritative for membership and payload updates. When an
 * in-flight item is still present in storage, preserve drain progress fields
 * (syncing status / display-until) from workingQueue on top of the stored item.
 */
const reconcileConcurrentQueueItems = (
  workingQueue,
  storedQueue,
  inFlightId
) => {
  const workingById = new Map(workingQueue.map((item) => [item.id, item]));

  return storedQueue.map((storedItem) => {
    const workingItem = workingById.get(storedItem.id);

    if (!workingItem || storedItem.id !== inFlightId) {
      return storedItem;
    }

    const reconciled = { ...storedItem };

    DRAIN_PROGRESS_FIELDS.forEach((field) => {
      if (Object.prototype.hasOwnProperty.call(workingItem, field)) {
        reconciled[field] = workingItem[field];
      }
    });

    return reconciled;
  });
};

const queueFromMap = (queueById) => Array.from(queueById.values());

const rebuildClockConnectPrediction = (userKey, queueItems) =>
  Promise.resolve(rebuildOfflinePunchPrediction(userKey, queueItems)).catch(
    () => false
  );

/**
 * Refresh the stored API payload. A failed request leaves the cache as is.
 */
const refetchMobileClockConnect = async (dispatch, user) => {
  let subscription;

  try {
    subscription = dispatch(
      webclockApi.endpoints.clockConnect.initiate(
        { mobile: true, user },
        { forceRefetch: true }
      )
    );
    await subscription;
  } catch {
    // Keep the cached API payload when Clock Connect is unreachable.
  } finally {
    if (isFunction(subscription?.unsubscribe)) {
      subscription.unsubscribe();
    }
  }
};

const updateQueueItemInMap = (queueById, itemId, updater) => {
  const current = queueById.get(itemId);

  if (!current) {
    return;
  }

  queueById.set(itemId, updater(current));
};

export default function OfflinePunchProvider({
  children,
  devIsOfflineOverride,
  onUnconfirmedSessionCleanup,
}) {
  const { isOfflineShellMode } = useIsOfflineShellMode();
  const dispatch = useDispatch();
  const user = useUser();
  const { authError, reportExpiredSession } = useBootstrapAuth();
  const reportExpiredSessionRef = useRef(reportExpiredSession);
  reportExpiredSessionRef.current = reportExpiredSession;
  const userKey = getWebClockUserStorageKey(user);
  const { submitOfflinePunch } = useSubmitOfflinePunch();
  const { openDrawer: rawOpenDrawer } = useDrawer(
    webClockContextKeys.OFFLINE_PUNCHES
  );
  const rawOpenDrawerRef = useRef(rawOpenDrawer);
  rawOpenDrawerRef.current = rawOpenDrawer;
  const openDrawer = useCallback(() => {
    rawOpenDrawerRef.current();
  }, []);

  const [queue, setQueue] = useState(() => {
    if (!isOfflineShellMode || !isOfflineSecureStorageWritable()) {
      return [];
    }

    return purgeExpiredItems(readOfflinePunchQueue(userKey));
  });
  const drainingRef = useRef(false);
  const drainAgainRef = useRef(false);
  const authBlockedRef = useRef(false);
  const isMountedRef = useRef(true);
  const retryTimeoutRef = useRef(null);
  const isOfflineShellModeRef = useRef(isOfflineShellMode);
  const isOnlineRef = useRef(true);
  const isConnectivityOnlineRef = useRef(true);
  const devIsOfflineOverrideRef = useRef(devIsOfflineOverride);
  const drainQueueRef = useRef(async () => {});
  const hydrationPromiseRef = useRef(Promise.resolve());
  const queueMutationGenerationRef = useRef(0);
  const queueOpChainRef = useRef(Promise.resolve());
  const submittedIdsRef = useRef(new Set());

  isOfflineShellModeRef.current = isOfflineShellMode;
  devIsOfflineOverrideRef.current = devIsOfflineOverride;

  // Serialize enqueue/delete/drain storage RMW so async iOS/devSecure writes
  // cannot race mergeIfMutated against an in-flight persist.
  const runQueueOperation = useCallback((operation) => {
    const run = queueOpChainRef.current.then(operation);
    queueOpChainRef.current = run.catch(() => {});
    return run;
  }, []);

  const getEffectiveIsOnline = useCallback((online) => {
    if (!isNil(devIsOfflineOverrideRef.current)) {
      return !devIsOfflineOverrideRef.current;
    }
    return online;
  }, []);

  const userKeyRef = useRef(userKey);
  const userRef = useRef(user);
  const dispatchRef = useRef(dispatch);
  userKeyRef.current = userKey;
  userRef.current = user;
  dispatchRef.current = dispatch;

  useEffect(() => {
    const syncOnlineRef = (nextReachability = getApiReachability()) => {
      const apiBlocksOnline =
        isOfflineShellModeRef.current && nextReachability === 'apiOffline';

      isOnlineRef.current =
        isConnectivityOnlineRef.current && !apiBlocksOnline;
    };

    syncOnlineRef();

    return subscribeApiReachability(syncOnlineRef);
  }, []);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      submittedIdsRef.current.clear();

      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    submittedIdsRef.current.clear();
  }, [userKey]);

  const persistQueue = useCallback(
    (nextQueue, storageKey = userKeyRef.current) => {
      const saved = writeOfflinePunchQueue(nextQueue, storageKey);

      const applySaved = (wasSaved) => {
        if (
          wasSaved &&
          isMountedRef.current &&
          userKeyRef.current === storageKey
        ) {
          setQueue(nextQueue);
        }

        return Boolean(wasSaved);
      };

      if (isFunction(saved?.then)) {
        return saved.then(applySaved);
      }

      return applySaved(saved);
    },
    []
  );

  const removeQueueItem = useCallback(
    async (id, storageKey = userKeyRef.current) =>
      runQueueOperation(async () => {
        const current = readOfflinePunchQueue(storageKey);
        const remainingQueue = current.filter((item) => item.id !== id);
        const saved = await persistQueue(remainingQueue, storageKey);

        if (saved) {
          queueMutationGenerationRef.current += 1;
        }

        return { remainingQueue, saved: Boolean(saved) };
      }),
    [persistQueue, runQueueOperation]
  );

  const deletePunch = useCallback(
    async (id) => {
      const storageKey = userKeyRef.current;
      const { remainingQueue, saved } = await removeQueueItem(id, storageKey);

      if (!saved) {
        return false;
      }

      await rebuildClockConnectPrediction(storageKey, remainingQueue);

      if (isOfflineShellModeRef.current && isOnlineRef.current) {
        refetchMobileClockConnect(dispatchRef.current, userRef.current);
      }

      return true;
    },
    [removeQueueItem]
  );

  const scheduleDrain = useCallback((delayMs = 0) => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
    }

    const timeoutId = setTimeout(() => {
      if (retryTimeoutRef.current !== timeoutId) {
        return;
      }
      retryTimeoutRef.current = null;
      drainQueueRef.current();
    }, delayMs);

    retryTimeoutRef.current = timeoutId;
  }, []);

  const drainQueue = useCallback(async () => {
    if (drainingRef.current) {
      drainAgainRef.current = true;
      return;
    }

    if (
      !isMountedRef.current ||
      !isOfflineShellModeRef.current ||
      !isOnlineRef.current ||
      authBlockedRef.current ||
      !isOfflineSecureStorageWritable()
    ) {
      return;
    }

    drainingRef.current = true;
    let drainUserKey;
    let workingQueue = [];
    let queueById = new Map();
    let seenGeneration = queueMutationGenerationRef.current;
    let durableSnapshotDirty = false;
    let durableSaveFailed = false;
    let workingQueueNeedsSync = false;
    let syncedAny = false;

    const syncWorkingQueueFromMap = () => {
      if (!workingQueueNeedsSync) return;
      workingQueue = queueFromMap(queueById);
      workingQueueNeedsSync = false;
    };

    const replaceWorkingQueue = (nextQueue) => {
      workingQueue = nextQueue;
      queueById = new Map(nextQueue.map((item) => [item.id, item]));
      workingQueueNeedsSync = false;
    };

    const persistDurableQueue = async () => {
      syncWorkingQueueFromMap();
      const saved = await persistQueue(workingQueue, drainUserKey);

      if (saved) {
        durableSnapshotDirty = false;
        durableSaveFailed = false;
        seenGeneration = queueMutationGenerationRef.current;
      } else {
        durableSaveFailed = true;
      }

      return saved;
    };

    const publishUiQueue = () => {
      syncWorkingQueueFromMap();

      if (isMountedRef.current && userKeyRef.current === drainUserKey) {
        setQueue(workingQueue);
      }
    };

    const mergeIfMutated = (inFlightId) => {
      if (queueMutationGenerationRef.current === seenGeneration) {
        return;
      }

      const storedQueue = readOfflinePunchQueue(drainUserKey);
      replaceWorkingQueue(
        reconcileConcurrentQueueItems(workingQueue, storedQueue, inFlightId)
      );
      seenGeneration = queueMutationGenerationRef.current;
      durableSnapshotDirty = true;
    };

    // Merge then persist under one lock acquisition so an in-flight enqueue
    // cannot be skipped by a stale generation check and then overwritten.
    const lockedMergeAndPersist = (inFlightId) =>
      runQueueOperation(async () => {
        mergeIfMutated(inFlightId);
        return persistDurableQueue();
      });

    const lockedMergeOnly = () =>
      runQueueOperation(async () => {
        mergeIfMutated();
      });

    const lockedAfterSuccessfulSubmit = (submittedItem) =>
      runQueueOperation(async () => {
        mergeIfMutated(submittedItem.id);
        queueById.delete(submittedItem.id);
        workingQueueNeedsSync = true;
        durableSnapshotDirty = true;
        publishUiQueue();

        // Persist immediately so unmount / userKey change cannot skip the
        // guarded final persist and leave a submitted item durable for replay.
        return persistDurableQueue();
      });

    const lockedAfterFailedSubmit = (failedItem, error) =>
      runQueueOperation(async () => {
        mergeIfMutated(failedItem.id);

        if (isAuthError(error)) {
          updateQueueItemInMap(queueById, failedItem.id, (item) => ({
            ...item,
            status: 'pending',
            awaitingAuth: true,
            lastError: getOfflinePunchErrorMessage(error),
            retryAfter: undefined,
          }));
          workingQueueNeedsSync = true;
          const authSaved = await persistDurableQueue();

          if (authSaved) {
            authBlockedRef.current = true;
          }

          if (isFunction(reportExpiredSessionRef.current)) {
            reportExpiredSessionRef.current();
          }

          return authSaved;
        }

        if (isTransportError(error)) {
          updateQueueItemInMap(queueById, failedItem.id, (item) => ({
            ...item,
            status: 'pending',
            awaitingAuth: undefined,
            lastError: getOfflinePunchErrorMessage(error),
          }));
          workingQueueNeedsSync = true;
          reportApiUnreachable();
          isOnlineRef.current = false;

          return persistDurableQueue();
        }

        // Classification order: auth, transport (above), business, network, unclassified.
        const isPermanentFailure =
          isBusinessError(error) || !isNetworkError(error);

        if (isPermanentFailure) {
          // Business rejections and unclassified failures are not retryable;
          // keep the punch visible as failed and move on to its siblings.
          updateQueueItemInMap(queueById, failedItem.id, (item) => ({
            ...item,
            status: 'permanent_failure',
            awaitingAuth: undefined,
            lastError: getOfflinePunchErrorMessage(error),
            retryAfter: undefined,
          }));
          workingQueueNeedsSync = true;
        } else {
          const retryCount = failedItem.retryCount + 1;
          const failureNow = Date.now();
          const delayMs = getNextRetryDelayMs(
            retryCount,
            failedItem.queuedAt,
            failureNow
          );
          const currentItem = queueById.get(failedItem.id);

          updateQueueItemInMap(queueById, failedItem.id, (item) => ({
            ...item,
            status: 'pending',
            retryCount,
            lastError: getOfflinePunchErrorMessage(error),
            retryAfter: failureNow + delayMs,
            retryDisplayUntil: extendRetryDisplayUntil(
              currentItem?.retryDisplayUntil,
              failureNow
            ),
          }));
          workingQueueNeedsSync = true;
        }

        return persistDurableQueue();
      });

    try {
      let pendingHydration = hydrationPromiseRef.current;

      while (pendingHydration) {
        // A user-key change replaces the in-flight hydration promise.
        // eslint-disable-next-line no-await-in-loop
        await pendingHydration;

        if (hydrationPromiseRef.current === pendingHydration) {
          break;
        }

        pendingHydration = hydrationPromiseRef.current;
      }

      if (
        !isMountedRef.current ||
        !isOfflineShellModeRef.current ||
        !isOnlineRef.current ||
        authBlockedRef.current ||
        !isOfflineSecureStorageWritable()
      ) {
        return;
      }

      drainUserKey = userKeyRef.current;

      const deferredItemIds = new Set();
      const initialized = await runQueueOperation(async () => {
        const initialQueue = readOfflinePunchQueue(drainUserKey);
        const nowAtStart = Date.now();
        replaceWorkingQueue(purgeExpiredItems(initialQueue, nowAtStart));

        if (workingQueue.length < initialQueue.length) {
          return persistDurableQueue();
        }

        return true;
      });

      if (!initialized) {
        return;
      }

      while (
        isMountedRef.current &&
        isOnlineRef.current &&
        userKeyRef.current === drainUserKey &&
        isOfflineSecureStorageWritable()
      ) {
        // eslint-disable-next-line no-await-in-loop
        await lockedMergeOnly();

        const now = Date.now();
        const beforePurgeLength = queueById.size;
        replaceWorkingQueue(purgeExpiredItems(workingQueue, now));

        if (queueById.size < beforePurgeLength) {
          durableSnapshotDirty = true;
        }

        const nextItem = workingQueue.find(
          (item) =>
            isReadyForRetry(item, now) &&
            !deferredItemIds.has(item.id) &&
            !submittedIdsRef.current.has(item.id)
        );

        if (!nextItem) {
          if (durableSnapshotDirty) {
            // eslint-disable-next-line no-await-in-loop
            const finalSaved = await lockedMergeAndPersist();

            if (!finalSaved) {
              break;
            }
          }

          const retryDelayMs = getEarliestRetryDelayMs(workingQueue, now);

          if (retryDelayMs !== null) {
            scheduleDrain(retryDelayMs);
          }

          break;
        }

        updateQueueItemInMap(queueById, nextItem.id, (item) => ({
          ...item,
          status: 'syncing',
          ...(nextItem.retryCount > 0
            ? { retryDisplayUntil: getRetryDisplayUntil(now) }
            : { submitDisplayUntil: getSubmitDisplayUntil(now) }),
        }));
        workingQueueNeedsSync = true;
        // UI-only: syncing is not durable. Crash mid-submit keeps the item pending.
        publishUiQueue();
        durableSnapshotDirty = true;

        submittedIdsRef.current.add(nextItem.id);

        try {
          // One in-flight offline punch at a time; continue to other ready items after each result.
          // Network submit stays outside the queue lock so enqueues can proceed.
          // eslint-disable-next-line no-await-in-loop
          await submitOfflinePunch(nextItem);
          syncedAny = true;

          // eslint-disable-next-line no-await-in-loop
          const concurrentSaved = await lockedAfterSuccessfulSubmit(nextItem);

          if (!concurrentSaved) {
            break;
          }

          try {
            // eslint-disable-next-line no-await-in-loop
            await Promise.resolve(
              recordCompletedMobilePunch(drainUserKey, {
                punch: nextItem.punch,
                punchDateTime: nextItem.punchDateTime,
              })
            );
          } catch (recordError) {
            console.warn(
              '[OfflinePunchProvider] Failed to record synced punch button state.',
              recordError
            );
          }
        } catch (error) {
          // eslint-disable-next-line no-await-in-loop
          const failureSaved = await lockedAfterFailedSubmit(nextItem, error);

          if (!failureSaved) {
            break;
          }

          if (authBlockedRef.current) {
            break;
          }

          if (isTransportError(error)) {
            break;
          }

          deferredItemIds.add(nextItem.id);
        } finally {
          submittedIdsRef.current.delete(nextItem.id);
        }
      }

      if (
        durableSnapshotDirty &&
        isMountedRef.current &&
        userKeyRef.current === drainUserKey &&
        !authBlockedRef.current
      ) {
        await lockedMergeAndPersist();
      }

      if (syncedAny && isOnlineRef.current && isOfflineShellModeRef.current) {
        refetchMobileClockConnect(dispatchRef.current, userRef.current);
      }
    } finally {
      drainingRef.current = false;
      const drainAgain = drainAgainRef.current;
      drainAgainRef.current = false;
      syncWorkingQueueFromMap();

      const hasReadyItem =
        isMountedRef.current &&
        isOnlineRef.current &&
        isOfflineShellModeRef.current &&
        userKeyRef.current === drainUserKey &&
        !authBlockedRef.current &&
        isOfflineSecureStorageWritable() &&
        hasReadyQueueItem(workingQueue, Date.now(), { alreadyPurged: true });

      if (
        isMountedRef.current &&
        !durableSaveFailed &&
        (drainAgain || hasReadyItem)
      ) {
        scheduleDrain(0);
      }
    }
  }, [persistQueue, runQueueOperation, scheduleDrain, submitOfflinePunch]);

  drainQueueRef.current = drainQueue;

  useOfflineWebClockLifecycle({
    authBlockedRef,
    authError,
    hydrationPromiseRef,
    isOfflineShellMode,
    isOnlineRef,
    onUnconfirmedSessionCleanup,
    persistQueue,
    runQueueOperation,
    scheduleDrain,
    setQueue,
    userKey,
    userKeyRef,
    userToken: user.token,
  });

  const { isOnline } = useNetworkStatus({
    onConnectivityChange: (online) => {
      isConnectivityOnlineRef.current = getEffectiveIsOnline(online);
      const apiBlocksOnline =
        isOfflineShellModeRef.current && getApiReachability() === 'apiOffline';

      isOnlineRef.current =
        isConnectivityOnlineRef.current && !apiBlocksOnline;
    },
  });

  const isConnectivityOnline = useMemo(
    () => getEffectiveIsOnline(isOnline),
    [getEffectiveIsOnline, isOnline, devIsOfflineOverride]
  );
  const reportProbeAuthFailure = useCallback(() => {
    authBlockedRef.current = true;

    if (isFunction(reportExpiredSessionRef.current)) {
      reportExpiredSessionRef.current();
    }
  }, [authBlockedRef]);
  const { isApiOffline } = useApiReachability({
    authBlockedRef,
    authError,
    isConnectivityOnline,
    isOfflineShellMode,
    onAuthFailure: reportProbeAuthFailure,
    user,
    userKey,
  });
  const apiBlocksOnline = isOfflineShellMode && isApiOffline;
  const effectiveIsOnline = isConnectivityOnline && !apiBlocksOnline;

  isConnectivityOnlineRef.current = isConnectivityOnline;
  isOnlineRef.current =
    isConnectivityOnline &&
    !(isOfflineShellMode && getApiReachability() === 'apiOffline');

  const {
    acknowledgeOfflineSnackbar,
    acknowledgeOnlineSnackbar,
    isOffline,
    isStatusUiEnabled,
    offlineSnackbarRequested,
    onlineSnackbarRequested,
    registerClosePersistentOfflineSnackbar,
  } = useOfflineConnectivitySnackbars({
    devIsOfflineOverride,
    effectiveIsOnline,
    isOfflineShellMode,
    onOnline: scheduleDrain,
  });

  const reportTransportFailure = useCallback(() => {
    if (!isOfflineShellModeRef.current) {
      return;
    }

    reportApiUnreachable();
    isOnlineRef.current = false;
  }, []);

  const enqueuePunch = useCallback(
    async (payload) => {
      if (!isOfflineShellMode) {
        throw new Error(
          'Offline punches are unavailable outside the native offline shell.'
        );
      }

      if (!isOfflineSecureStorageWritable()) {
        throw new Error(
          'Offline punch storage is unavailable. Please contact your administrator.'
        );
      }

      const enqueueUserKey = userKeyRef.current;
      await hydrationPromiseRef.current;

      if (!hasValue(enqueueUserKey) || userKeyRef.current !== enqueueUserKey) {
        return { saved: false };
      }

      const savedQueue = await runQueueOperation(async () => {
        if (userKeyRef.current !== enqueueUserKey) {
          return null;
        }

        const current = readOfflinePunchQueue(enqueueUserKey);
        const itemId = payload.punchSessionId ?? payload.id;
        const existingIndex = current.findIndex((item) => item.id === itemId);
        const nextItem = buildQueueItem(payload, isOfflineShellMode);

        if (
          existingIndex < 0 &&
          current.length >= OFFLINE_PUNCH_QUEUE_MAX_ITEMS
        ) {
          throw new Error(
            'Offline punch queue is full. Please sync existing punches or contact your administrator.'
          );
        }

        const nextQueue =
          existingIndex >= 0
            ? current.map((item, index) =>
                index === existingIndex ? { ...item, ...nextItem } : item
              )
            : [...current, nextItem];

        const saved = await persistQueue(nextQueue, enqueueUserKey);
        if (!saved) {
          throw new Error(
            'Unable to save your punch offline. Please try again or contact your administrator.'
          );
        }

        queueMutationGenerationRef.current += 1;
        return nextQueue;
      });

      if (!savedQueue) {
        return { saved: false };
      }

      const patched = await rebuildClockConnectPrediction(
        enqueueUserKey,
        savedQueue
      );

      if (isOnlineRef.current) {
        scheduleDrain(0);
      }

      return { saved: true, cachePatched: patched !== false };
    },
    [isOfflineShellMode, persistQueue, runQueueOperation, scheduleDrain]
  );

  const contextValue = useMemo(
    () => ({
      queue,
      pendingCount: countPendingItems(queue),
      hasFailedPunch: hasFailedOfflinePunch(queue),
      isOffline,
      isStatusUiEnabled,
      offlineSnackbarRequested,
      onlineSnackbarRequested,
      enqueuePunch,
      deletePunch,
      openDrawer,
      reportApiUnreachable: reportTransportFailure,
      registerClosePersistentOfflineSnackbar,
      acknowledgeOfflineSnackbar,
      acknowledgeOnlineSnackbar,
    }),
    [
      queue,
      isOffline,
      isStatusUiEnabled,
      offlineSnackbarRequested,
      onlineSnackbarRequested,
      enqueuePunch,
      deletePunch,
      openDrawer,
      reportTransportFailure,
      registerClosePersistentOfflineSnackbar,
      acknowledgeOfflineSnackbar,
      acknowledgeOnlineSnackbar,
    ]
  );

  return (
    <OfflinePunchContext.Provider value={contextValue}>
      {children}
    </OfflinePunchContext.Provider>
  );
}

OfflinePunchProvider.propTypes = {
  children: PropTypes.node.isRequired,
  devIsOfflineOverride: PropTypes.bool,
  onUnconfirmedSessionCleanup: PropTypes.func,
};
